import React from "react";
import {
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Spin,
  Switch,
} from "antd";
import { TagOutlined } from "@ant-design/icons";
import { CURRENCY_OPTIONS, CYCLE_OPTIONS } from "./lib/couponHelpers";

export default function CouponFormModal({
  open,
  editingId,
  form,
  formLoading,
  saving,
  plansLoading,
  planOptions,
  onCancel,
  onSubmit,
}) {
  const discountType = Form.useWatch("discount_type", form) || "percent";
  const isPercentDiscount = discountType === "percent";
  const isEdit = Boolean(editingId);

  return (
    <Modal
      className="billing-coupons-modal"
      title={isEdit ? "Edit Coupon" : "Create Coupon"}
      open={open}
      onCancel={onCancel}
      width={680}
      centered
      destroyOnClose
      footer={[
        <button
          key="cancel"
          type="button"
          className="btn btn-light fs-7 fw-bold me-2"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>,
        <button
          key="save"
          type="button"
          className="btn btn-danger fs-7 fw-bold text-white"
          onClick={() => form.submit()}
          disabled={saving || formLoading}
        >
          {saving ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create"}
        </button>,
      ]}
    >
      <Spin spinning={formLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          requiredMark={false}
          className="billing-coupons-form"
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Code"
                name="code"
                rules={[
                  { required: true, message: "Enter a coupon code" },
                  {
                    pattern: /^[A-Za-z0-9_-]+$/,
                    message: "Invalid characters in code",
                  },
                ]}
              >
                <Input
                  className="billing-coupons-form-input"
                  placeholder="WELCOME10"
                  maxLength={32}
                  disabled={isEdit}
                  prefix={<TagOutlined className="billing-coupons-input-icon" />}
                  onChange={(e) => {
                    if (!isEdit) {
                      form.setFieldValue("code", e.target.value.toUpperCase());
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: "Enter a coupon name" }]}
              >
                <Input
                  className="billing-coupons-form-input"
                  placeholder="Welcome discount"
                  maxLength={120}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <Input.TextArea
              className="billing-coupons-form-textarea"
              placeholder="Optional"
              maxLength={500}
              rows={2}
              showCount
            />
          </Form.Item>

          <Form.Item label="Type" name="discount_type">
            <Radio.Group className="billing-coupons-type-toggle">
              <Radio.Button value="percent">Percentage</Radio.Button>
              <Radio.Button value="fixed_amount">Fixed amount</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label={isPercentDiscount ? "Discount (%)" : "Amount (₹)"}
                name="discount_value"
                dependencies={["discount_type"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value == null || value === "") {
                        return Promise.reject(new Error("Enter discount"));
                      }
                      const type = getFieldValue("discount_type");
                      if (type === "percent" && (value < 1 || value > 100)) {
                        return Promise.reject(new Error("Must be 1–100"));
                      }
                      if (type === "fixed_amount" && value < 1) {
                        return Promise.reject(new Error("Must be at least ₹1"));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber
                  className="billing-coupons-form-input"
                  min={1}
                  max={isPercentDiscount ? 100 : undefined}
                  placeholder="10"
                  addonAfter={isPercentDiscount ? "%" : "₹"}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Usage limit"
                name="usage_limit"
                rules={[
                  {
                    validator(_, value) {
                      if (value == null || value === "") {
                        return Promise.reject(new Error("Enter usage limit"));
                      }
                      if (value < 1) {
                        return Promise.reject(new Error("Must be at least 1"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  className="billing-coupons-form-input"
                  min={1}
                  placeholder="20"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Per user"
                name="usage_per_user"
                rules={[
                  {
                    validator(_, value) {
                      if (value == null || value === "") {
                        return Promise.reject(new Error("Enter per-user limit"));
                      }
                      if (value < 1) {
                        return Promise.reject(new Error("Must be at least 1"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  className="billing-coupons-form-input"
                  min={1}
                  placeholder="1"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Min order (₹)" name="min_order_amount">
                <InputNumber
                  className="billing-coupons-form-input"
                  min={0}
                  placeholder="500"
                  addonAfter="₹"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Currency" name="currency">
                <Select
                  className="billing-coupons-plans-select"
                  options={CURRENCY_OPTIONS}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Valid from" name="valid_from">
                <DatePicker
                  className="billing-coupons-form-input billing-coupons-form-datepicker w-100"
                  format="DD/MM/YYYY"
                  placeholder="Optional"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Valid until"
                name="valid_until"
                dependencies={["valid_from"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const from = getFieldValue("valid_from");
                      if (!value || !from) return Promise.resolve();
                      if (value.isBefore(from, "day")) {
                        return Promise.reject(new Error("Must be after valid from"));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker
                  className="billing-coupons-form-input billing-coupons-form-datepicker w-100"
                  format="DD/MM/YYYY"
                  placeholder="Optional"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Plans"
            name="applicable_plans"
            rules={[
              {
                validator: (_, value) =>
                  Array.isArray(value) && value.length
                    ? Promise.resolve()
                    : Promise.reject(new Error("Select at least one plan")),
              },
            ]}
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Select plans"
              loading={plansLoading}
              optionLabelProp="label"
              maxTagCount="responsive"
              className="billing-coupons-plans-select"
              popupClassName="billing-coupons-plans-dropdown"
              filterOption={(input, option) =>
                (option?.searchText || "").includes(input.toLowerCase())
              }
              notFoundContent={
                plansLoading ? <Spin size="small" /> : "No plans found"
              }
              options={planOptions}
            />
          </Form.Item>

          <Form.Item
            label="Billing cycles"
            name="applicable_cycles"
            rules={[
              {
                validator: (_, value) =>
                  Array.isArray(value) && value.length
                    ? Promise.resolve()
                    : Promise.reject(new Error("Select at least one cycle")),
              },
            ]}
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Select cycles"
              className="billing-coupons-plans-select"
              options={CYCLE_OPTIONS}
            />
          </Form.Item>

          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
}
